import { TargetPriceChart } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePriceHistory } from '~/react-query-hooks/prices/usePriceHistory';
import { useMultipleSnakesMeta } from '~/react-query-hooks/snakes/useMultipleSnakesMeta';

import { TargetItem } from './target-item/TargetItem';
import * as S from './TargetPriceSection.styles';
import {
  getOneYearAgoDate,
  getTargetPriceValues,
  TARGET_PRICE_SNAKES,
} from './utils';

interface TargetPriceSectionProps {
  entityTag: string;
}

export const TargetPriceSection = ({ entityTag }: TargetPriceSectionProps) => {
  const { t } = useTranslation(['earnings']);

  const snakeKeys = Object.values(TARGET_PRICE_SNAKES).map(
    snake => `${entityTag}.${snake}`
  );
  const { data = [], isFetching } = useMultipleSnakesMeta(snakeKeys);

  const { data: priceData, isFetching: isPriceFetching } = usePriceHistory({
    entityTag,
  });

  if (isFetching || isPriceFetching) {
    return <S.StyledLoader />;
  }

  const oneYearAgoDate = getOneYearAgoDate();
  const filteredPriceData = (priceData?.data ?? []).filter(
    price => price.time >= oneYearAgoDate
  );

  const mostRecentPrice = filteredPriceData[filteredPriceData.length - 1];

  if (!mostRecentPrice) {
    return <S.StyledLoader />;
  }

  const { high, average, low } = getTargetPriceValues({
    data,
    mostRecentPrice: mostRecentPrice.close,
    entityTag,
  });

  const averageTargetChange =
    ((average.value ?? 0) - mostRecentPrice.close) / mostRecentPrice.close;

  const chartTimeseries = filteredPriceData.map(price => ({
    date: price.time,
    price: price.close,
  }));

  return (
    <S.Root>
      <S.TitleLabel>
        {t('earnings:earningsInsight.drawer.earningsPreview.targetPrice')}
      </S.TitleLabel>
      <S.FlexRow>
        <TargetPriceChart
          timeseries={chartTimeseries}
          targetPrice={{
            high: high.value ?? 0,
            average: average.value ?? 0,
            low: low.value ?? 0,
          }}
        />
        <S.TargetsContainer>
          <TargetItem
            titleKey="highest"
            value={high.value}
            isNegative={high.isNegative}
            currency={high.currency}
          />
          <TargetItem
            titleKey="average"
            value={average.value}
            isNegative={average.isNegative}
            currency={average.currency}
            isActive
            changeValue={averageTargetChange}
            showChange
          />
          <TargetItem
            titleKey="lowest"
            value={low.value}
            isNegative={low.isNegative}
            currency={low.currency}
          />
        </S.TargetsContainer>
      </S.FlexRow>
    </S.Root>
  );
};
